import { memo, useDeferredValue, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Users, Briefcase, Percent, Clock, UserPlus, Building2, Wrench, Truck, HardHat, ChevronDown, ChevronUp, ArrowLeft } from 'lucide-react';
import { api } from '../services/api';
import { useAuth } from '../context/AuthContext';
import LoadingSpinner from '../components/Shared/LoadingSpinner';
import PageHeader from '../components/Shared/PageHeader';
import MultiSelect from '../components/ContactCenter/MultiSelect';
import VacancyTable from '../components/Staffing/VacancyTable';
import NewHireTable from '../components/Staffing/NewHireTable';
import MechanicSkillTable from '../components/Staffing/MechanicSkillTable';
import MechanicMetrics from '../components/Staffing/MechanicMetrics';
import DriverQualificationTable from '../components/Staffing/DriverQualificationTable';
import HelperRosterTable from '../components/Staffing/HelperRosterTable';
import StaffingSummaryTable from '../components/Staffing/StaffingSummaryTable';
import { buildCcEntries, rollup } from '../components/Staffing/ccGroups';
import { fmtNumber, fmtPercent, fmtDate, categoryColor } from '../components/Staffing/format';
import { useSort, sortRows, SortTh, HeaderTintContext } from '../components/Shared/sortable';
import { useFacetFilters, FacetFilterBar } from '../components/Shared/facetFilter';
import ExpandableScroll from '../components/Shared/ExpandableScroll';
import { useRowLimit, ShowMoreRow } from '../components/Shared/rowLimit';
import { c, t, sp, card, btn, tbl } from '../theme';

const ccLabel = (r) => (r.cc_code ? `${r.cc_code} — ${r.cc_name}` : null);

// "Job Profile" and "Supervisor" are the labels used throughout this page
// for job_profile/manager — Workday's own terms for these fields,
// standardized from an earlier mix of "Job Profile"/"Job Title"/"Position"
// and "Manager"/"Supervisor" across these facet lists and their tables'
// column headers — all four fields, every table.
const VACANCY_FACETS = [
  { key: 'job_profile', label: 'Job Profile', get: (v) => v.job_profile },
  { key: 'location',    label: 'Primary Location', get: (v) => v.raw_location },
  { key: 'cost_center',  label: 'Cost Center', get: ccLabel },
  { key: 'pay_rate',    label: 'Pay Rate Type', get: (v) => v.pay_rate_type },
  { key: 'status',      label: 'Status', get: (v) => v.requisition_status },
  { key: 'offer_signed', label: 'Offer Signed', get: (v) => (v.offer_signed ? 'Offer Signed' : 'Not Signed') },
];
const NEW_HIRE_FACETS = [
  { key: 'job_profile', label: 'Job Profile', get: (h) => h.job_profile },
  { key: 'cost_center', label: 'Cost Center', get: ccLabel },
  { key: 'manager',     label: 'Supervisor', get: (h) => h.manager },
];
const MECHANIC_FACETS = [
  { key: 'cost_center', label: 'Cost Center', get: ccLabel },
  { key: 'job_profile', label: 'Job Profile', get: (m) => m.job_profile },
  { key: 'manager',     label: 'Supervisor', get: (m) => m.manager },
];
const DRIVER_FACETS = [
  { key: 'cost_center', label: 'Cost Center', get: ccLabel },
  { key: 'job_profile', label: 'Job Profile', get: (d) => d.job_profile },
  { key: 'manager',     label: 'Supervisor', get: (d) => d.manager },
];
const HELPER_FACETS = [
  { key: 'cost_center', label: 'Cost Center', get: ccLabel },
  { key: 'job_profile', label: 'Job Profile', get: (h) => h.job_profile },
  { key: 'manager',     label: 'Supervisor', get: (h) => h.manager },
];

// Company-wide (or filtered-subset) rollup for the Mechanic Development
// metrics cards — same math as computeMechanicKpis/computeTierSummary in
// routes/staffing.js, operating here on whatever is currently visible
// (focused cost centers, local facet filters) rather than the single
// facility a per-cost-center detail request would be scoped to. Each row
// already carries rating/expectation/benchmark/tier_key/tier_label
// precomputed server-side — this just aggregates them.
function computeMechanicKpis(mechanics) {
  const scored = mechanics.filter((m) => m.rating !== null && m.rating !== undefined);
  const withTarget = scored.filter((m) => m.benchmark !== null && m.benchmark !== undefined);
  const meetingCount = withTarget.filter((m) => m.benchmark >= 0).length;
  return {
    total: mechanics.length,
    scored_count: scored.length,
    avg_rating: scored.length ? Math.round((scored.reduce((a, m) => a + m.rating, 0) / scored.length) * 100) / 100 : null,
    avg_benchmark: withTarget.length ? Math.round((withTarget.reduce((a, m) => a + m.benchmark, 0) / withTarget.length) * 100) / 100 : null,
    pct_meeting_expectation: withTarget.length ? Math.round((meetingCount / withTarget.length) * 1000) / 10 : null,
  };
}
function computeTierSummary(mechanics) {
  const groups = new Map();
  for (const m of mechanics) {
    if (!m.tier_key) continue;
    if (!groups.has(m.tier_key)) groups.set(m.tier_key, { label: m.tier_label, target: m.expectation, ratings: [] });
    if (m.rating !== null && m.rating !== undefined) groups.get(m.tier_key).ratings.push(m.rating);
  }
  return [...groups.values()]
    .map((g) => ({
      label: g.label, target: g.target, scored_count: g.ratings.length,
      avg_rating: g.ratings.length ? Math.round((g.ratings.reduce((a, b) => a + b, 0) / g.ratings.length) * 100) / 100 : null,
    }))
    .sort((a, b) => b.target - a.target);
}

const CATEGORIES = ['Driver', 'Mechanic', 'Operations', 'Admin'];

// One header colour for every table on this page, the same light blue the
// Summary's Headcount columns use.
const STAFFING_HEADER = { background: c.infoTint, color: c.textMuted };

// The Roster's first column gets a coloured left border, so it trades 3px of
// its left padding for it. Written as one padding value, not a paddingLeft over
// tbl.th/td's padding shorthand: that mix is what React warns about when the
// first column changes as the focus goes from one cost center to several.
const ROSTER_FIRST_PAD = '9px 14px 9px 11px';

const YARD_SKILL_COLUMNS = [
  { key: 'engine',                  label: 'Engine' },
  { key: 'transmission',            label: 'Transmission' },
  { key: 'drivetrain',              label: 'Drivetrain' },
  { key: 'suspension_steering',     label: 'Suspension & Steering' },
  { key: 'brakes',                  label: 'Brakes' },
  { key: 'electrical_system',       label: 'Electrical System' },
  { key: 'heating_ac',              label: 'Heating & AC' },
  { key: 'fuel_system_cng',         label: 'Fuel System CNG' },
  { key: 'hydraulics',              label: 'Hydraulics' },
  { key: 'lube_greasing',           label: 'Lube & Greasing' },
  { key: 'truck_washing',           label: 'Truck Washing' },
  { key: 'tires',                   label: 'Tires' },
  { key: 'cummins_allison_davie',   label: 'Cummins/Allison/DAVIE' },
  { key: 'pm_inspection_knowledge', label: 'PM Inspection Knowledge' },
];

const MRF_SKILL_COLUMNS = [
  { key: 'administration',    label: 'Administration' },
  { key: 'cleaning',          label: 'Cleaning' },
  { key: 'electrical_system', label: 'Electrical System' },
  { key: 'fabrication',       label: 'Fabrication' },
  { key: 'heating_ac',        label: 'Heating & AC' },
  { key: 'hydraulic_system',  label: 'Hydraulic System' },
  { key: 'lube_greasing',     label: 'Lube & Greasing' },
  { key: 'mechanical_system', label: 'Mechanical System' },
  { key: 'plumbing',          label: 'Plumbing' },
  { key: 'pneumatic_system',  label: 'Pneumatic System' },
  { key: 'troubleshooting',   label: 'Troubleshooting' },
];

const SECTION_ICON = {
  vacancies: c.info,
  newHires:  c.success,
  yard:      '#C2410C',
  mrf:       '#7C3AED',
  driver:    '#0E7490',
  helper:    '#A16207',
  cc:        c.textMuted,
  roster:    c.textMuted,
};

export default function StaffingTracker() {
  const { isAdmin, user } = useAuth();
  // Empty = every cost center this user can see — MultiSelect's emptyMeansAll.
  // Holds selector keys: a cost center id, or "group:admin" / "group:sbc" for
  // the merged Admin (100-125) and SBC (500-599) options.
  const [ccFilters, setCcFilters] = useState([]);
  // The selector reflects a click at once; everything below it follows from the
  // deferred copy at lower priority. Re-drawing the page's tables takes long
  // enough that, tied to the same update, the checkbox itself lagged behind the
  // click -- which is what made picking a cost center feel janky.
  const deferredFilters = useDeferredValue(ccFilters);

  const canEdit = isAdmin || !!user?.canViewStaffingTracker;

  const { data: summary, isLoading: summaryLoading, error } = useQuery({
    queryKey: ['staffing-summary'],
    queryFn:  () => api.get('/staffing/summary'),
  });

  const ccEntries = useMemo(() => buildCcEntries(summary?.by_cost_center), [summary]);

  if (summaryLoading) return <LoadingSpinner />;

  const kpis = summary?.kpis || {};
  const hasData = Number(kpis.headcount || 0) > 0 || Number(kpis.vacancy_count || 0) > 0;

  // By code, not by name: sorting names alphabetically scatters each
  // facility block across the whole list even though the leading code already
  // groups them. A merged option sorts by its lowest code (Admin up top, SBC
  // after the 4xx facilities).
  const ccEntriesByCode = [...ccEntries].sort((a, b) => a.minCode - b.minCode);
  const labelByKey = new Map(ccEntries.map((e) => [e.key, e.label]));

  const ccFilterControl = (
    <div style={s.filterRow}>
      {ccFilters.length > 0 && (
        <button type="button" style={s.backBtn} onClick={() => setCcFilters([])}>
          <ArrowLeft size={13} /> Summary
        </button>
      )}
      <MultiSelect
        label="Cost Center" options={ccEntriesByCode.map((e) => e.key)} selected={ccFilters} onChange={setCcFilters}
        allLabel="All Cost Centers" noun="selected" emptyMeansAll align="right"
        displayFn={(key) => labelByKey.get(key) || key}
      />
    </div>
  );

  return (
    <HeaderTintContext.Provider value={STAFFING_HEADER}>
      <div style={s.page}>
        <PageHeader
          eyebrow="Recruiting"
          title="Athens Staffing & Development Tracker"
          subtitle="Current staff, open requisitions, and new-hire onboarding progress, sourced from Workday. Access here follows the same cost centers assigned for Overtime, Fuel Report, and Inventory."
          actions={ccFilterControl}
          alignActions="end"
        />

        {error || !hasData ? (
          <div style={s.emptyCard}>
            <Users size={28} color={c.textFaint} />
            <div style={s.message}>
              {error ? (error.message || 'You do not have access to the Staffing Tracker.') : 'No staffing data has been imported yet.'}
            </div>
            {canEdit && !error && (
              <div style={s.emptyHint}>Import the tracker from Administration &rarr; Staffing Tracker Upload.</div>
            )}
          </div>
        ) : (
          <div style={{ opacity: deferredFilters === ccFilters ? 1 : 0.55, transition: 'opacity 0.15s' }}>
            <StaffingBody ccFilters={deferredFilters} ccEntries={ccEntries} kpis={kpis} canEdit={canEdit} onFocus={setCcFilters} />
          </div>
        )}
      </div>
    </HeaderTintContext.Provider>
  );
}

// Memoized so a click in the selector (which changes ccFilters, not the
// deferred copy handed down here) re-renders only the control, not every table.
const StaffingBody = memo(function StaffingBody({ ccFilters, ccEntries, kpis, canEdit, onFocus }) {
  const [category, setCategory] = useState('all');
  const [showFilled, setShowFilled] = useState(false);

  const entryByKey = new Map(ccEntries.map((e) => [e.key, e]));
  const focusedEntries = ccFilters.map((k) => entryByKey.get(k)).filter(Boolean);
  const focusedSet = new Set(focusedEntries.flatMap((e) => e.ccIds));
  const hasFocus = ccFilters.length > 0;
  // Admin-only focus: Recruiting only works Admin's job reqs, so everything
  // that is about people in the yards is left off.
  const adminOnly = hasFocus && focusedEntries.length > 0 && focusedEntries.every((e) => e.isAdmin);
  const vacancyCategory = adminOnly ? 'Admin' : category;

  // These lists already cover every cost center a user can see in one call —
  // focusing on a subset is a client-side filter on cost_center_id, not a
  // different request, same reason HeadcountTab's focus filter works this way.
  const { data: vacancyList, isLoading: vacancyListLoading } = useQuery({
    queryKey: ['staffing-vacancies', vacancyCategory],
    queryFn:  () => api.get('/staffing/vacancies', { params: vacancyCategory !== 'all' ? { category: vacancyCategory } : {} }),
  });
  const { data: newHireList, isLoading: newHireListLoading } = useQuery({
    queryKey: ['staffing-new-hires'],
    queryFn:  () => api.get('/staffing/new-hires'),
  });
  const { data: yardMechList, isLoading: yardMechListLoading } = useQuery({
    queryKey: ['staffing-mechanics-yard'],
    queryFn:  () => api.get('/staffing/mechanics/yard'),
  });
  const { data: mrfMechList, isLoading: mrfMechListLoading } = useQuery({
    queryKey: ['staffing-mechanics-mrf'],
    queryFn:  () => api.get('/staffing/mechanics/mrf'),
  });
  const { data: driverList, isLoading: driverListLoading } = useQuery({
    queryKey: ['staffing-drivers'],
    queryFn:  () => api.get('/staffing/drivers'),
  });
  const { data: helperList, isLoading: helperListLoading } = useQuery({
    queryKey: ['staffing-helpers'],
    queryFn:  () => api.get('/staffing/helpers'),
  });

  // The Roster is only ever shown for a focused facility, and it is a few
  // thousand rows company-wide, so it is fetched once (and only then) like the
  // lists above instead of once per selected cost center.
  const { data: rosterList, isLoading: rosterListLoading } = useQuery({
    queryKey: ['staffing-roster'],
    queryFn:  () => api.get('/staffing/roster'),
    enabled:  hasFocus && !adminOnly,
  });

  // Every hook — including useFacetFilters below — has to run before any
  // early return (summaryLoading, further down), or React loses track of
  // hook order between renders.
  const filterByCc = (rows) => (hasFocus ? rows.filter((row) => focusedSet.has(row.cost_center_id)) : rows);
  // Positions already in offer / background check / hire (is_filled, decided
  // in routes/staffing.js FILLED_POSITION_SQL) are not vacancies to Recruiting:
  // hidden here and left out of the metrics, but one tick away so a req
  // marked by mistake can still be found and un-marked.
  const allVacanciesByCc = filterByCc(vacancyList?.vacancies || []);
  const filledCount = allVacanciesByCc.filter((v) => v.is_filled).length;
  const visibleVacanciesByCc = showFilled ? allVacanciesByCc : allVacanciesByCc.filter((v) => !v.is_filled);
  const visibleNewHiresByCc  = filterByCc(newHireList?.new_hires || []);
  const visibleYardMechByCc  = filterByCc(yardMechList?.mechanics || []);
  const visibleMrfMechByCc   = filterByCc(mrfMechList?.mechanics || []);
  const visibleDriversByCc  = filterByCc(driverList?.drivers || []);
  const visibleHelpersByCc  = filterByCc(helperList?.helpers || []);

  // Local facet filters (Job Profile, Manager, Cost Center, ...) further
  // narrow whatever the cost-center focus already produced — independent
  // state per table, same reasoning HeadcountTab's focus filter and this
  // page's own cost-center MultiSelect already established: a flat list
  // spanning every facility is long enough to want a second, finer filter
  // without leaving this view.
  const vacancyFacets = useFacetFilters(visibleVacanciesByCc, VACANCY_FACETS);
  const newHireFacets = useFacetFilters(visibleNewHiresByCc, NEW_HIRE_FACETS);
  const yardMechFacets = useFacetFilters(visibleYardMechByCc, MECHANIC_FACETS);
  const mrfMechFacets = useFacetFilters(visibleMrfMechByCc, MECHANIC_FACETS);
  const driverFacets = useFacetFilters(visibleDriversByCc, DRIVER_FACETS);
  const helperFacets = useFacetFilters(visibleHelpersByCc, HELPER_FACETS);
  const visibleVacancies = vacancyFacets.filtered;
  const visibleNewHires = newHireFacets.filtered;
  const visibleDrivers = driverFacets.filtered;
  const visibleHelpers = helperFacets.filtered;
  const visibleYardMech = yardMechFacets.filtered;
  const visibleMrfMech = mrfMechFacets.filtered;

  // A facility with no MRF mechanics (or no drivers, ...) has nothing to show
  // in that section, so it is left off rather than rendered empty. Judged on
  // the rows before the local facet filters, so narrowing a filter to nothing
  // never makes the section (and the filter to undo it) vanish.
  const showYard    = !adminOnly && (yardMechListLoading || visibleYardMechByCc.length > 0);
  const showMrf     = !adminOnly && (mrfMechListLoading || visibleMrfMechByCc.length > 0);
  const showDrivers = !adminOnly && (driverListLoading || visibleDriversByCc.length > 0);
  const showHelpers = !adminOnly && (helperListLoading || visibleHelpersByCc.length > 0);
  const focusedRoster = (rosterList?.roster || []).filter((r) => focusedSet.has(r.cost_center_id));

  // The metrics cards reflect whatever is currently visible after BOTH the
  // cost-center focus and the local facet filters above — computed
  // client-side (see computeMechanicKpis/computeTierSummary above) rather
  // than read from a per-cost-center detail request, so they never go stale
  // relative to the table they sit above and show something even in the
  // "all cost centers" admin view, which the single-cost-center detail
  // endpoint never covered.
  const yardKpis = computeMechanicKpis(visibleYardMech);
  const yardTiers = computeTierSummary(visibleYardMech);
  const mrfKpis = computeMechanicKpis(visibleMrfMech);
  const mrfTiers = computeTierSummary(visibleMrfMech);

  // Recomputed from the same per-cost-center rows the Summary shows, the same
  // way HeadcountTab's focus filter recomputes its own Total row — a
  // company-wide KPI would not match a narrowed-down page. Average days open is
  // weighted by each cost center's vacancy count.
  const visibleKpis = hasFocus ? rollup(focusedEntries) : kpis;

  return (
        <>
          <div style={s.kpiRow}>
            <KPICard icon={<Users size={16} />} label="Headcount" value={fmtNumber(visibleKpis.headcount)} />
            <KPICard icon={<Briefcase size={16} />} label="Open Vacancies" value={fmtNumber(visibleKpis.vacancy_count)} />
            <KPICard icon={<Percent size={16} />} label="Vacancy Rate" value={fmtPercent(visibleKpis.vacancy_rate)} />
            <KPICard icon={<Clock size={16} />} label="Avg Days Open" value={fmtNumber(visibleKpis.avg_days_open)} />
          </div>

          {!hasFocus && (
            <Section icon={<Building2 size={15} color={SECTION_ICON.cc} />} title="Summary" count={ccEntries.length}>
              <StaffingSummaryTable entries={ccEntries} onFocus={onFocus} />
            </Section>
          )}

          <Section
            icon={<Briefcase size={15} color={SECTION_ICON.vacancies} />}
            title={adminOnly ? 'Admin Vacancies' : 'Vacancies'} count={visibleVacancies.length}
            right={(
              <div style={s.filterRow}>
                {(filledCount > 0 || showFilled) && (
                  <label style={s.checkLabel} title="Requisitions with an offer, a candidate in background check, or a hire pending count as filled, not as vacancies">
                    <input type="checkbox" checked={showFilled} onChange={(e) => setShowFilled(e.target.checked)} />
                    Show filled ({fmtNumber(filledCount)})
                  </label>
                )}
                {!adminOnly && (
                  <select style={s.selectSm} value={category} onChange={(e) => setCategory(e.target.value)}>
                    <option value="all">All Categories</option>
                    {CATEGORIES.map((cat) => <option key={cat} value={cat}>{cat}</option>)}
                  </select>
                )}
              </div>
            )}
          >
            <FacetFilterBar rows={visibleVacanciesByCc} facets={VACANCY_FACETS} selected={vacancyFacets.selected} setFacet={vacancyFacets.setFacet} />
            {vacancyListLoading ? <LoadingSpinner /> : (
              <VacancyTable vacancies={visibleVacancies} canEdit={canEdit} />
            )}
          </Section>

          <Section icon={<UserPlus size={15} color={SECTION_ICON.newHires} />} title="New Hires (last 35 days)" count={visibleNewHires.length}>
            <FacetFilterBar rows={visibleNewHiresByCc} facets={NEW_HIRE_FACETS} selected={newHireFacets.selected} setFacet={newHireFacets.setFacet} />
            {newHireListLoading ? <LoadingSpinner /> : (
              <NewHireTable newHires={visibleNewHires} canEdit={canEdit} />
            )}
          </Section>

          {showYard && (
          <Section icon={<Wrench size={15} color={SECTION_ICON.yard} />} title="Yard Mechanic Development" count={visibleYardMech.length}>
            <div style={s.metricsWrap}>
              <MechanicMetrics kpis={yardKpis} tierSummary={yardTiers} title="Yard" />
            </div>
            <FacetFilterBar rows={visibleYardMechByCc} facets={MECHANIC_FACETS} selected={yardMechFacets.selected} setFacet={yardMechFacets.setFacet} />
            {yardMechListLoading ? <LoadingSpinner /> : (
              <MechanicSkillTable
                mechanics={visibleYardMech} canEdit={canEdit}
                skillColumns={YARD_SKILL_COLUMNS} patchUrlBase="/staffing/mechanics/yard"
                queryKey="staffing-mechanics-yard" showPromotionDate
                emptyLabel="No Yard mechanics here."
              />
            )}
          </Section>
          )}

          {showMrf && (
          <Section icon={<Wrench size={15} color={SECTION_ICON.mrf} />} title="MRF Mechanic Development" count={visibleMrfMech.length}>
            <div style={s.metricsWrap}>
              <MechanicMetrics kpis={mrfKpis} tierSummary={mrfTiers} title="MRF" />
            </div>
            <FacetFilterBar rows={visibleMrfMechByCc} facets={MECHANIC_FACETS} selected={mrfMechFacets.selected} setFacet={mrfMechFacets.setFacet} />
            {mrfMechListLoading ? <LoadingSpinner /> : (
              <MechanicSkillTable
                mechanics={visibleMrfMech} canEdit={canEdit}
                skillColumns={MRF_SKILL_COLUMNS} patchUrlBase="/staffing/mechanics/mrf"
                queryKey="staffing-mechanics-mrf" showPromotionDate showShieldRides={false}
                emptyLabel="No MRF mechanics here."
              />
            )}
          </Section>
          )}

          {showDrivers && (
          <Section icon={<Truck size={15} color={SECTION_ICON.driver} />} title="Driver Development" count={visibleDrivers.length}>
            <FacetFilterBar rows={visibleDriversByCc} facets={DRIVER_FACETS} selected={driverFacets.selected} setFacet={driverFacets.setFacet} />
            {driverListLoading ? <LoadingSpinner /> : (
              <DriverQualificationTable drivers={visibleDrivers} canEdit={canEdit} />
            )}
          </Section>
          )}

          {showHelpers && (
          <Section icon={<HardHat size={15} color={SECTION_ICON.helper} />} title="Helper / Trainee Roster" count={visibleHelpers.length}>
            <FacetFilterBar rows={visibleHelpersByCc} facets={HELPER_FACETS} selected={helperFacets.selected} setFacet={helperFacets.setFacet} />
            {helperListLoading ? <LoadingSpinner /> : (
              <HelperRosterTable helpers={visibleHelpers} canEdit={canEdit} />
            )}
          </Section>
          )}

          {hasFocus && !adminOnly && (
            <Section
              icon={<Users size={15} color={SECTION_ICON.roster} />}
              title="Roster" count={focusedRoster.length} defaultOpen={false}
            >
              {rosterListLoading ? <LoadingSpinner /> : <RosterTable rows={focusedRoster} multiCc={focusedSet.size > 1} />}
            </Section>
          )}
        </>
  );
});

// Collapsible by default — `right` sits outside the toggle button so a
// control like the Vacancies category picker never also fires the collapse.
function Section({ icon, title, right, children, count, defaultOpen = true }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div style={{ ...card.base, overflow: 'hidden', marginBottom: sp[5] }}>
      <div style={s.sectionHead}>
        <button type="button" style={s.sectionToggle} onClick={() => setOpen((v) => !v)}>
          {open ? <ChevronUp size={15} color={c.textMuted} /> : <ChevronDown size={15} color={c.textMuted} />}
          <span style={s.sectionHeadTitle}>{icon} {title}</span>
          {count != null && <span style={s.rosterCount}>{fmtNumber(count)}</span>}
        </button>
        {right}
      </div>
      {open && children}
    </div>
  );
}

// A roster row that one of the focused cost centers masked (can_view_employees
// false for that facility) simply arrives without worker_name/employee_id —
// it renders as "—" rather than a blank cell or a crash.
function RosterTable({ rows, multiCc }) {
  const sort = useSort();
  const sorted = sortRows(rows, sort.key, sort.dir, {
    worker_name:  (r0) => r0.worker_name || '',
    start_date:   (r0) => r0.start_date || '',
    job_profile:  (r0) => r0.job_profile || '',
    manager:      (r0) => r0.manager || '',
    category:     (r0) => r0.job_requisition_category || '',
    cost_center:  (r0) => r0.cc_name || '',
  });
  const showPeople = rows.some((r) => 'worker_name' in r);
  const limiter = useRowLimit(sorted);
  return (
    <ExpandableScroll collapsedHeight={520}>
      <table style={s.table}>
        <thead>
          <tr>
            {multiCc && <SortTh label="Cost Center" sortKey="cost_center" sort={sort} sticky style={{ ...tbl.th, padding: ROSTER_FIRST_PAD }} />}
            {showPeople && <SortTh label="Worker" sortKey="worker_name" sort={sort} sticky style={multiCc ? undefined : { ...tbl.th, padding: ROSTER_FIRST_PAD }} />}
            {showPeople && <SortTh label="Employee ID" sticky />}
            {showPeople && <SortTh label="Start Date" sortKey="start_date" sort={sort} sticky />}
            <SortTh label="Job Profile" sortKey="job_profile" sort={sort} sticky style={(!multiCc && !showPeople) ? { ...tbl.th, padding: ROSTER_FIRST_PAD } : undefined} />
            <SortTh label="Manager" sortKey="manager" sort={sort} sticky />
            <SortTh label="Category" sortKey="category" sort={sort} sticky />
          </tr>
        </thead>
        <tbody>
          {limiter.visible.map((r, i) => {
            const firstBorder = { borderLeft: `3px solid ${categoryColor(r.job_requisition_category).border}`, padding: ROSTER_FIRST_PAD };
            const isFirstCol = { cc: multiCc, worker: !multiCc && showPeople, profile: !multiCc && !showPeople };
            return (
              <tr key={r.id} style={tbl.trZebra(i)}>
                {multiCc && <td style={{ ...tbl.td, ...(isFirstCol.cc ? firstBorder : null) }}>{r.cc_code ? `${r.cc_code} — ${r.cc_name}` : '—'}</td>}
                {showPeople && <td style={{ ...tbl.td, ...(isFirstCol.worker ? firstBorder : null) }}>{r.worker_name || '—'}</td>}
                {showPeople && <td style={tbl.td}>{r.worker_employee_id || '—'}</td>}
                {showPeople && <td style={tbl.td}>{fmtDate(r.start_date)}</td>}
                <td style={{ ...tbl.td, ...(isFirstCol.profile ? firstBorder : null) }}>{r.job_profile || '—'}</td>
                <td style={tbl.td}>{r.manager || '—'}</td>
                <td style={tbl.td}>{r.job_requisition_category || '—'}</td>
              </tr>
            );
          })}
          <ShowMoreRow limiter={limiter} colSpan={multiCc ? 7 : 6} />
          {!rows.length && (
            <tr><td colSpan={multiCc ? 7 : 6} style={s.empty}>No current staff at the focused cost center(s).</td></tr>
          )}
        </tbody>
      </table>
    </ExpandableScroll>
  );
}

function KPICard({ icon, label, value }) {
  return (
    <div style={s.kpiCard}>
      <div style={s.kpiIcon}>{icon}</div>
      <div>
        <div style={s.kpiValue}>{value}</div>
        <div style={s.kpiLabel}>{label}</div>
      </div>
    </div>
  );
}

const s = {
  page:        { width: '100%' },
  emptyCard:   { ...card.base, padding: '48px 24px', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: sp[3], textAlign: 'center' },
  message:     { fontSize: t.bodySm.fontSize, color: c.textMuted },
  emptyHint:   { fontSize: t.caption.fontSize, color: c.textFaint },
  filterRow:   { display: 'flex', alignItems: 'center', gap: sp[2] },
  checkLabel:  { display: 'inline-flex', alignItems: 'center', gap: '6px', fontSize: t.caption.fontSize, color: c.textMuted, cursor: 'pointer' },
  backBtn:     { ...btn.secondaryXs, display: 'inline-flex', alignItems: 'center', gap: '5px' },
  selectSm:    { padding: '5px 8px', border: `1px solid ${c.borderStrong}`, borderRadius: '6px', fontSize: t.caption.fontSize, color: c.textStrong, fontFamily: 'inherit', background: c.surface },
  kpiRow:      { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(170px, 1fr))', gap: sp[3], marginBottom: sp[5] },
  kpiCard:     { ...card.base, padding: '16px 18px', display: 'flex', alignItems: 'center', gap: sp[3] },
  kpiIcon:     { width: '34px', height: '34px', borderRadius: '8px', background: c.infoTint, color: c.info, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 },
  kpiValue:    { fontSize: t.h2.fontSize, fontWeight: 700, color: c.text },
  kpiLabel:    { fontSize: t.caption.fontSize, color: c.textMuted, marginTop: '1px' },
  sectionHead: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: sp[2], ...card.head },
  sectionToggle: {
    display: 'flex', alignItems: 'center', gap: sp[2],
    border: 'none', background: 'none', cursor: 'pointer', fontFamily: 'inherit', padding: 0,
  },
  sectionHeadTitle: { display: 'flex', alignItems: 'center', gap: sp[2], fontSize: t.bodySm.fontSize, fontWeight: 700, color: c.text },
  rosterCount: { fontSize: t.caption.fontSize, fontWeight: 600, color: c.textMuted, background: c.canvas, borderRadius: '10px', padding: '2px 8px', marginLeft: '2px' },
  metricsWrap: { padding: '14px 18px', background: c.surfaceSunk, borderBottom: `1px solid ${c.border}` },
  table:       { width: '100%', borderCollapse: 'collapse', fontSize: t.bodySm.fontSize },
  empty:       { textAlign: 'center', padding: '24px', color: c.textFaint },
};
